<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="clientDirty.id ? 'Edit client' : 'Add new client'"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          Client Name
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="clientDirty.name"
            outlined
            :error-messages="errors"
            placeholder="Enter client name"
          />
        </validation-provider>
        <v-row align="center">
          <v-col class="font-weight-bold">
            Proxy player downloads
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="clientDirty.proxy_player_downloads"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Yes
              </v-btn>
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                No
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <div v-if="clientDirty.proxy_player_downloads">
          <div class="mb-2 font-weight-bold">
            Player proxy download speed (Mbps)
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="clientDirty.player_proxy_download_speed"
              outlined
              :items="download_speeds.data"
              :loading="download_speeds.loading"
              item-value="speed"
              item-text="speed"
              :error-messages="errors"
              placeholder="Select download speed"
            />
          </validation-provider>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="clientDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteClient()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import ClientModel from '@/models/ClientModel'
import Clients from '@/services/Clients'

export default {
  name: 'EditClientDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    client: {
      type: Object,
      default: () => cloneDeep(ClientModel.emptyModel)
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      clientDirty: cloneDeep(this.client),
      submitting: false,
      download_speeds: {
        loading: false,
        data: [
          { speed: 1 },
          { speed: 2 },
          { speed: 5 }
        ]
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    modalVisible(visible) {
      if (!visible) return

      this.clientDirty = cloneDeep(this.client)
    }
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.clientDirty.name,
          proxy_player_downloads: this.clientDirty.proxy_player_downloads,
          player_proxy_download_speed: this.clientDirty.player_proxy_download_speed
        }

        if (this.clientDirty.id) {
          const result = await Clients.update(this.clientDirty.id, data)

          this.$emit('client:updated', result)

          this.$store.dispatch('alert', {
            text: 'Client successfully updated',
            color: 'success'
          })
        } else {
          const result = await Clients.store(data)

          this.$emit('client:created', result)

          this.$store.dispatch('alert', {
            text: 'Client successfully created',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting client',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteClient() {
      this.deleting = true

      try {
        const result = await Clients.delete(this.clientDirty.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('client:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }
}
</script>
